import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import PaywallNotice from "@/components/PaywallNotice";
import HandwrittenUpload from "@/components/HandwrittenUpload";
import {
  FileText, Clock, Award, TrendingUp, Target, Zap, Play, CheckCircle2, 
  AlertCircle, Download, Eye, Bookmark, BookmarkCheck, Calendar, BarChart3,
  Camera, X, Upload, Loader2, ImageIcon, Plus, ArrowUp, ArrowDown, Pencil, Image
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const TestSeriesPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [view, setView] = useState("home"); // home, generate, taking-test, results, history
  const [testMode, setTestMode] = useState("exam"); // exam, practice
  const [testConfig, setTestConfig] = useState({
    paper: "GS1",
    test_type: "sectional",
    difficulty: "medium",
    num_questions: 10,
    duration_minutes: 120
  });
  const [currentTest, setCurrentTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [bookmarked, setBookmarked] = useState({});
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [incompleteTests, setIncompleteTests] = useState([]);
  const [userRank, setUserRank] = useState(null);
  // Handwritten upload states
  const [answerModes, setAnswerModes] = useState({}); // { questionId: 'typed' | 'handwritten' }
  const [handwrittenData, setHandwrittenData] = useState({}); // { questionId: { extractedText, evaluationData } }

  // Auth guard - redirect if logged out during test
  useEffect(() => {
    if (!user || !token) {
      navigate('/auth', { replace: true });
    }
  }, [user, token, navigate]);

  const isPro = user?.role === "PRO";

  // Test Templates
  const testTemplates = [
    {
      name: "GS Paper 1 - Full",
      paper: "GS1",
      questions: 20,
      duration: 180,
      difficulty: "hard",
      icon: "📚",
      color: "indigo"
    },
    {
      name: "GS Paper 2 - Quick",
      paper: "GS2",
      questions: 10,
      duration: 90,
      difficulty: "medium",
      icon: "⚖️",
      color: "blue"
    },
    {
      name: "GS Paper 3 - Full",
      paper: "GS3",
      questions: 20,
      duration: 180,
      difficulty: "hard",
      icon: "🏭",
      color: "emerald"
    },
    {
      name: "GS Paper 4 - Ethics",
      paper: "GS4",
      questions: 15,
      duration: 120,
      difficulty: "medium",
      icon: "🎯",
      color: "purple"
    },
    {
      name: "Essay Writing",
      paper: "Essay",
      questions: 2,
      duration: 180,
      difficulty: "hard",
      icon: "✍️",
      color: "rose"
    }
  ];

  useEffect(() => {
    if (isPro) {
      loadAttempts();
      loadPerformanceData();
      loadIncompleteTests();
      loadUserRank();
    }
  }, [isPro]);

  // Timer effect
  useEffect(() => {
    if (view === "taking-test" && testMode === "exam" && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [view, testMode, timeRemaining]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (view === "taking-test" && currentTest && Object.keys(answers).length > 0) {
      const autoSaveTimer = setInterval(async () => {
        try {
          await managedAxios.post(`${API_BASE}/test-series/save-draft`, {
            test_id: currentTest.id,
            answers: answers
          });
          console.log("Draft auto-saved");
        } catch (err) {
          console.error("Auto-save failed:", err);
        }
      }, 30000); // Every 30 seconds
      
      return () => clearInterval(autoSaveTimer);
    }
  }, [view, currentTest, answers]);

  const loadAttempts = async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/test-series/attempts?limit=10`);
      setAttempts(data || []);
    } catch (err) {
      console.error("Error loading attempts:", err);
    }
  };

  const loadPerformanceData = async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/test-series/attempts?limit=50`);
      // Transform data for charts
      const chartData = data.map((attempt, idx) => ({
        test: `Test ${data.length - idx}`,
        score: Math.round(attempt.percentage || 0),
        date: new Date(attempt.submitted_at || attempt.attempted_at).toLocaleDateString(),
        paper: attempt.paper
      }));
      setPerformanceData(chartData.reverse());
    } catch (err) {
      console.error("Error loading performance data:", err);
    }
  };

  const loadIncompleteTests = async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/test-series/incomplete`);
      setIncompleteTests(data || []);
    } catch (err) {
      console.error("Error loading incomplete tests:", err);
      setIncompleteTests([]);
    }
  };

  const loadUserRank = async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/test-series/leaderboard/overall`);
      setUserRank(data.your_rank);
    } catch (err) {
      console.error("Error loading rank:", err);
    }
  };

  const handleQuickStart = (template) => {
    setTestConfig({
      paper: template.paper,
      test_type: "sectional",
      difficulty: template.difficulty,
      num_questions: template.questions,
      duration_minutes: template.duration
    });
    handleGenerateTest({
      paper: template.paper,
      test_type: "sectional",
      difficulty: template.difficulty,
      num_questions: template.questions,
      duration_minutes: template.duration
    });
  };

  const handleGenerateTest = async (config = testConfig) => {
    if (!isPro) {
      toast({
        variant: "destructive",
        description: "🔒 AI Test Series is PRO-only. Upgrade to unlock."
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/test-series/generate`, config);
      setCurrentTest(data);
      setAnswers({});
      setBookmarked({});
      // Initialize answer modes and handwritten data for each question
      const initialModes = {};
      const initialHandwritten = {};
      data.questions?.forEach((q, idx) => {
        const qId = q.id || idx;
        initialModes[qId] = 'typed'; // Default to typed answers
        initialHandwritten[qId] = null;
      });
      setAnswerModes(initialModes);
      setHandwrittenData(initialHandwritten);
      setStartTime(Date.now());
      setTimeRemaining(config.duration_minutes * 60);
      setView("taking-test");
      toast({ description: "✓ Test generated successfully!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not generate test." });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTest = async () => {
    if (!currentTest) return;

    const timeSpent = Math.round((Date.now() - startTime) / 60000); // minutes

    setLoading(true);
    try {
      const submission = {
        test_id: currentTest.id,
        answers: Object.keys(answers).map(qId => ({
          question_id: qId,
          answer_text: answers[qId],
          // Include handwritten data if available
          answer_mode: answerModes[qId] || 'typed',
          handwritten_evaluation: handwrittenData[qId]?.evaluationData || null
        })),
        time_spent_minutes: timeSpent
      };

      const { data } = await managedAxios.post(`${API_BASE}/test-series/submit`, submission);
      setTestResult(data);
      setView("results");
      loadAttempts();
      toast({ description: "✓ Test submitted! Generating detailed analysis..." });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not submit test." });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (attemptId) => {
    try {
      toast({ description: "🔄 Generating downloadable report..." });
      
      // Fetch full attempt details if not already loaded
      let attempt = attempts.find(a => a.id === attemptId) || testResult;
      
      // If attempt doesn't have evaluated_answers, fetch full details
      if (attempt && (!attempt.evaluated_answers || attempt.evaluated_answers.length === 0)) {
        try {
          const { data } = await managedAxios.get(`${API_BASE}/test-series/attempt/${attemptId}`);
          attempt = data;
        } catch (err) {
          console.error("Error fetching full attempt:", err);
        }
      }
      
      if (!attempt) {
        toast({ variant: "destructive", description: "❌ Test attempt not found" });
        return;
      }

      // Create a comprehensive text report
      const reportContent = `
╔════════════════════════════════════════════════════════════════════════════╗
║                     RANKER-UPSC-AI TEST REPORT                             ║
╚════════════════════════════════════════════════════════════════════════════╝

TEST INFORMATION
────────────────────────────────────────────────────────────────────────────
Test Title       : ${attempt.title || 'AI Generated Test'}
Paper            : ${attempt.paper || 'N/A'}
Difficulty       : ${attempt.difficulty ? attempt.difficulty.toUpperCase() : 'N/A'}
Date Attempted   : ${new Date(attempt.submitted_at || attempt.attempted_at || Date.now()).toLocaleString()}
Time Spent       : ${attempt.time_spent_minutes} minutes

PERFORMANCE SUMMARY
────────────────────────────────────────────────────────────────────────────
Overall Score    : ${attempt.score || attempt.total_score || 0}/${attempt.total_marks}
Percentage       : ${Math.round(attempt.percentage || 0)}%
Grade            : ${attempt.percentage >= 75 ? 'A+' : attempt.percentage >= 60 ? 'A' : attempt.percentage >= 50 ? 'B' : attempt.percentage >= 40 ? 'C' : 'D'}


════════════════════════════════════════════════════════════════════════════
                         QUESTION-WISE ANALYSIS
════════════════════════════════════════════════════════════════════════════

${attempt.evaluated_answers?.map((qa, idx) => `
────────────────────────────────────────────────────────────────────────────
QUESTION ${idx + 1}
────────────────────────────────────────────────────────────────────────────

${qa.question_text || 'Question text not available'}

YOUR ANSWER:
${qa.user_answer || 'Not answered'}

MODEL ANSWER:
${qa.model_answer || 'Model answer not available'}

EVALUATION:
Score Obtained   : ${qa.score}/${qa.max_marks} marks
Feedback         : ${qa.feedback || 'No feedback available'}

STRENGTHS:
${qa.strengths?.map(s => `  ✓ ${s}`).join('\n') || '  • None identified'}

AREAS FOR IMPROVEMENT:
${qa.improvements?.map(i => `  ➤ ${i}`).join('\n') || '  • None identified'}

`).join('\n') || 'No question-wise analysis available'}

════════════════════════════════════════════════════════════════════════════
                          OVERALL ANALYSIS
════════════════════════════════════════════════════════════════════════════

STRONG AREAS:
${attempt.analytics?.strong_topics?.map(t => `  ✓ ${t}`).join('\n') || '  • None identified'}

WEAK AREAS:
${attempt.analytics?.weak_topics?.map(t => `  ⚠ ${t}`).join('\n') || '  • None identified'}

PERSONALIZED IMPROVEMENT PLAN:
${attempt.improvement_plan?.map((p, i) => `  ${i + 1}. ${p}`).join('\n') || '  • No recommendations available'}


════════════════════════════════════════════════════════════════════════════
                      RECOMMENDATIONS FOR NEXT STEPS
════════════════════════════════════════════════════════════════════════════

1. Review weak areas identified above
2. Practice more questions from challenging topics
3. Analyze model answers to understand expected structure
4. Take another test to track improvement

────────────────────────────────────────────────────────────────────────────
Report Generated on: ${new Date().toLocaleString()}
Generated by: RANKER-UPSC-AI Test Series Engine
────────────────────────────────────────────────────────────────────────────
`;

      // Create and download the file
      const blob = new Blob([reportContent], { type: 'text/plain; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      
      // Create filename with date and paper name
      const dateStr = new Date(attempt.submitted_at || attempt.attempted_at || Date.now())
        .toISOString().split('T')[0];
      const paperName = (attempt.paper || 'test').replace(/\s+/g, '-');
      a.download = `RANKER-Test-Report-${paperName}-${dateStr}.txt`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ 
        description: "✅ Report downloaded successfully!",
        duration: 3000
      });
    } catch (err) {
      console.error("Download error:", err);
      toast({ 
        variant: "destructive", 
        description: "❌ Could not download report. Please try again." 
      });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleBookmark = (questionId) => {
    setBookmarked(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Toggle between typed and handwritten answer modes
  const toggleAnswerMode = (questionId) => {
    setAnswerModes(prev => ({
      ...prev,
      [questionId]: prev[questionId] === 'typed' ? 'handwritten' : 'typed'
    }));
  };

  // Handle handwritten evaluation completion for a specific question
  const handleHandwrittenEvaluation = (questionId, evaluationData) => {
    setHandwrittenData(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        evaluationData
      }
    }));
    // Also update the answer text with extracted content
    if (evaluationData?.extracted_answer) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: evaluationData.extracted_answer
      }));
    }
    toast({
      description: `✅ Handwritten answer for Q${questionId} processed!`
    });
  };

  // Handle extracted data from handwritten upload
  const handleExtractedData = (questionId, data) => {
    setHandwrittenData(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        extractedText: data.answer
      }
    }));
    // Update the answer text with extracted content
    if (data.answer) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: data.answer
      }));
    }
  };

  if (!isPro) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <PaywallNotice
          title="AI Test Series is PRO-only"
          description="Upgrade to CLARIAS PRO to unlock full-length GS1–4, Essay and CSAT test series with AI scoring, analytics, and All India Rankings."
          testIdPrefix="test-series-paywall"
        />
      </div>
    );
  }

  // Home View
  if (view === "home") {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <FileText className="w-8 h-8" />
                AI Test Series Engine
              </h1>
              <p className="text-violet-100">
                Full-length tests with AI scoring, detailed analytics, and downloadable reports
              </p>
            </div>
            <Badge className="px-4 py-2 text-sm bg-amber-500">
              ⭐ PRO Access
            </Badge>
          </div>
        </div>

        {/* Quick Stats with AIR */}
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-4 space-y-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <p className="text-2xl font-bold text-blue-900">{attempts.length}</p>
              <p className="text-xs font-semibold text-blue-900">Tests Taken</p>
            </CardContent>
          </Card>

          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4 space-y-2">
              <Award className="w-5 h-5 text-emerald-600" />
              <p className="text-2xl font-bold text-emerald-900">
                {attempts && attempts.length > 0 && attempts[0]?.percentage 
                  ? `${Math.round(Number(attempts[0].percentage))}%` 
                  : '--'}
              </p>
              <p className="text-xs font-semibold text-emerald-900">Best Score</p>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
            <CardContent className="p-4 space-y-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <p className="text-2xl font-bold text-purple-900">
                {attempts && attempts.length > 0 
                  ? `${Math.round(attempts.reduce((sum, a) => sum + (Number(a?.percentage) || 0), 0) / attempts.length)}%` 
                  : '--'}
              </p>
              <p className="text-xs font-semibold text-purple-900">Avg Score</p>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-4 space-y-2">
              <Target className="w-5 h-5 text-amber-600" />
              <p className="text-2xl font-bold text-amber-900">
                {userRank ? `#${userRank}` : '--'}
              </p>
              <p className="text-xs font-semibold text-amber-900">All India Rank</p>
            </CardContent>
          </Card>

          <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
            <CardContent className="p-4 space-y-2">
              <BarChart3 className="w-5 h-5 text-rose-600" />
              <p className="text-2xl font-bold text-rose-900">
                {attempts.length > 0 ? `${Math.round(attempts.length * 1.5)}h` : '--'}
              </p>
              <p className="text-xs font-semibold text-rose-900">Total Hours</p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Trend Chart */}
        {performanceData && performanceData.length > 2 && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">📈 Performance Trends</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="test" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      name="Score %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-slate-600">
                    {performanceData.length} tests analyzed
                  </span>
                </div>
                <div className="text-slate-600">
                  Latest: {performanceData[performanceData.length - 1]?.score}%
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resume Incomplete Tests */}
        {incompleteTests && incompleteTests.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">⏸️ Resume Incomplete Tests</h2>
              <div className="space-y-3">
                {incompleteTests.map((test, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-4 rounded-lg border border-amber-200">
                    <div>
                      <h3 className="font-semibold text-slate-900">{test.title || `Test ${idx + 1}`}</h3>
                      <p className="text-sm text-slate-600">
                        Last saved: {new Date(test.last_saved).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        onClick={() => {
                          // Resume test logic
                          toast({ description: "Resuming test..." });
                          setView("taking-test");
                        }}
                      >
                        Resume
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={async () => {
                          try {
                            await managedAxios.delete(`${API_BASE}/test-series/incomplete/${test.test_id}`);
                            loadIncompleteTests();
                            toast({ description: "Draft deleted" });
                          } catch (err) {
                            toast({ variant: "destructive", description: "Could not delete draft" });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Start Templates */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">🚀 Quick Start Templates</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {testTemplates.map((template, idx) => (
              <Card 
                key={idx} 
                className="border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => handleQuickStart(template)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{template.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-1">{template.name}</h3>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge variant="outline">{template.questions} Qs</Badge>
                        <Badge variant="outline">{template.duration} mins</Badge>
                        <Badge variant="outline" className="capitalize">{template.difficulty}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setView("generate")}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-indigo-600 p-4 rounded-xl">
                  <Play className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-indigo-900">Custom Test Generator</h3>
                  <p className="text-sm text-indigo-700">Create AI test with custom settings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setView("history")}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-purple-600 p-4 rounded-xl">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-purple-900">Test History</h3>
                  <p className="text-sm text-purple-700">View & download past tests</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Attempts */}
        {attempts && attempts.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">📊 Recent Test Attempts (Last 5)</h2>
            <div className="space-y-3">
              {attempts.slice(0, 5).map((attempt, idx) => (
                <Card key={idx} className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{attempt.title || `Test ${idx + 1}`}</h3>
                        <div className="flex gap-4 text-xs text-slate-600 mt-1">
                          <span>📅 {new Date(attempt.submitted_at).toLocaleDateString()}</span>
                          <span>⏱️ {attempt.time_spent_minutes} mins</span>
                          <span>📝 {attempt.paper}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-indigo-600">{Math.round(attempt.percentage)}%</p>
                          <p className="text-xs text-slate-600">{attempt.score}/{attempt.total_marks}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedAttempt(attempt);
                              setTestResult(attempt);
                              setView("results");
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDownloadReport(attempt.id)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Generate Test View
  if (view === "generate") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Custom Test Generator</h1>
          <Button variant="outline" onClick={() => setView("home")}>← Back</Button>
        </div>

        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Test Mode Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Test Mode</label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={testMode === "exam" ? "default" : "outline"}
                  onClick={() => setTestMode("exam")}
                  className="justify-start"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Exam Mode (Timed)
                </Button>
                <Button
                  variant={testMode === "practice" ? "default" : "outline"}
                  onClick={() => setTestMode("practice")}
                  className="justify-start"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Practice Mode
                </Button>
              </div>
            </div>

            {/* Paper Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Select Paper</label>
              <select
                value={testConfig.paper}
                onChange={(e) => setTestConfig({ ...testConfig, paper: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="GS1">GS Paper 1 - Indian Heritage & Culture</option>
                <option value="GS2">GS Paper 2 - Governance & Polity</option>
                <option value="GS3">GS Paper 3 - Economic Development</option>
                <option value="GS4">GS Paper 4 - Ethics & Integrity</option>
                <option value="Essay">Essay Writing</option>
              </select>
            </div>

            {/* Test Type */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Test Type</label>
              <select
                value={testConfig.test_type}
                onChange={(e) => setTestConfig({ ...testConfig, test_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="sectional">Sectional Test</option>
                <option value="full_length">Full Length Mock</option>
              </select>
            </div>

            {/* Difficulty */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Difficulty Level</label>
              <select
                value={testConfig.difficulty}
                onChange={(e) => setTestConfig({ ...testConfig, difficulty: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            {/* Number of Questions */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Number of Questions</label>
              <Input
                type="number"
                min="5"
                max="50"
                value={testConfig.num_questions}
                onChange={(e) => setTestConfig({ ...testConfig, num_questions: parseInt(e.target.value) })}
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Duration (minutes)</label>
              <Input
                type="number"
                min="30"
                max="300"
                value={testConfig.duration_minutes}
                onChange={(e) => setTestConfig({ ...testConfig, duration_minutes: parseInt(e.target.value) })}
              />
            </div>

            <Button 
              className="w-full" 
              size="lg"
              onClick={() => handleGenerateTest()}
              disabled={loading}
            >
              {loading ? "Generating Test..." : "Generate Test"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Taking Test View
  if (view === "taking-test" && currentTest) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Test Header with Timer */}
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{currentTest.title}</h1>
                <p className="text-sm text-slate-600">{currentTest.paper} • {currentTest.questions?.length || 0} Questions</p>
              </div>
              <div className="flex items-center gap-4">
                {testMode === "exam" && (
                  <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border-2 border-indigo-300">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    <span className={`text-lg font-bold ${
                      timeRemaining < 300 ? 'text-red-600' : 'text-indigo-900'
                    }`}>
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                )}
                <Button onClick={handleSubmitTest} disabled={loading}>
                  Submit Test
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <div className="space-y-6">
          {currentTest.questions?.map((q, idx) => {
            const questionId = q.id || idx;
            const currentMode = answerModes[questionId] || 'typed';
            const hasHandwrittenData = handwrittenData[questionId]?.evaluationData;
            
            return (
              <Card key={questionId} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge variant="outline">Q{idx + 1}</Badge>
                        <Badge variant="outline">{q.marks || 10} marks</Badge>
                        {q.topic && <Badge variant="outline" className="bg-purple-50">{q.topic}</Badge>}
                        {hasHandwrittenData && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Handwritten Evaluated
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-900 font-medium">{q.question_text || q.question}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleBookmark(questionId)}
                    >
                      {bookmarked[questionId] ? (
                        <BookmarkCheck className="w-5 h-5 text-indigo-600" />
                      ) : (
                        <Bookmark className="w-5 h-5 text-slate-400" />
                      )}
                    </Button>
                  </div>

                  {/* Answer Mode Toggle */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm text-slate-600">Answer Mode:</span>
                    <div className="flex bg-slate-100 rounded-lg p-1">
                      <Button
                        size="sm"
                        variant={currentMode === 'typed' ? 'default' : 'ghost'}
                        className={`text-xs px-3 py-1 h-7 ${currentMode === 'typed' ? 'bg-indigo-600 text-white' : ''}`}
                        onClick={() => setAnswerModes(prev => ({ ...prev, [questionId]: 'typed' }))}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Type
                      </Button>
                      <Button
                        size="sm"
                        variant={currentMode === 'handwritten' ? 'default' : 'ghost'}
                        className={`text-xs px-3 py-1 h-7 ${currentMode === 'handwritten' ? 'bg-indigo-600 text-white' : ''}`}
                        onClick={() => setAnswerModes(prev => ({ ...prev, [questionId]: 'handwritten' }))}
                      >
                        <Image className="w-3 h-3 mr-1" />
                        Upload Handwritten
                      </Button>
                    </div>
                  </div>

                  {/* Typed Answer Mode */}
                  {currentMode === 'typed' && (
                    <Textarea
                      placeholder="Write your answer here..."
                      value={answers[questionId] || ""}
                      onChange={(e) => setAnswers({ ...answers, [questionId]: e.target.value })}
                      rows={8}
                      className="w-full"
                    />
                  )}

                  {/* Handwritten Upload Mode */}
                  {currentMode === 'handwritten' && (
                    <div className="space-y-4">
                      <HandwrittenUpload
                        marks={q.marks || 10}
                        suite={currentTest.paper || "Test"}
                        onEvaluationComplete={(evalData) => handleHandwrittenEvaluation(questionId, evalData)}
                        onExtractedData={(data) => handleExtractedData(questionId, data)}
                        disabled={loading}
                      />
                      
                      {/* Show extracted text if available */}
                      {answers[questionId] && handwrittenData[questionId]?.extractedText && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-semibold text-emerald-800">Extracted Answer</span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                            {answers[questionId].substring(0, 500)}
                            {answers[questionId].length > 500 && '...'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Submit Button at Bottom */}
        <Card className="border-indigo-200 bg-indigo-50 sticky bottom-4">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="text-sm text-slate-700">
              <span className="font-semibold">{Object.keys(answers).length}</span> of <span className="font-semibold">{currentTest.questions?.length || 0}</span> questions answered
            </div>
            <Button onClick={handleSubmitTest} disabled={loading} size="lg">
              {loading ? "Submitting..." : "Submit for AI Evaluation"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results View with Detailed Analysis
  if (view === "results" && testResult) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">📊 Detailed Test Analysis</h1>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => handleDownloadReport(testResult.id)}>
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </Button>
            <Button onClick={() => setView("home")}>← Back to Home</Button>
          </div>
        </div>

        {/* Score Card with AIR */}
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 via-purple-50 to-fuchsia-50">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-5 gap-6">
              <div className="text-center">
                <Award className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-indigo-900">{Math.round(testResult.percentage)}%</p>
                <p className="text-sm text-slate-600 mt-1">Overall Score</p>
              </div>
              <div className="text-center">
                <Target className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-emerald-900">{testResult.score}/{testResult.total_marks}</p>
                <p className="text-sm text-slate-600 mt-1">Marks Obtained</p>
              </div>
              <div className="text-center">
                <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-amber-900">{testResult.time_spent_minutes}</p>
                <p className="text-sm text-slate-600 mt-1">Minutes Spent</p>
              </div>
              <div className="text-center">
                <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-purple-900">
                  {testResult.percentage >= 75 ? 'A+' : testResult.percentage >= 60 ? 'A' : testResult.percentage >= 50 ? 'B' : 'C'}
                </p>
                <p className="text-sm text-slate-600 mt-1">Grade</p>
              </div>
              <div className="text-center">
                <BarChart3 className="w-8 h-8 text-rose-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-rose-900">
                  {userRank ? `#${userRank}` : '--'}
                </p>
                <p className="text-sm text-slate-600 mt-1">All India Rank</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overall Analytics */}
        {testResult.analytics && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Overall Performance Analysis</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-emerald-700 mb-2">✅ Strong Areas</h3>
                  <ul className="space-y-1">
                    {testResult.analytics.strong_topics?.map((topic, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-amber-700 mb-2">📚 Areas for Improvement</h3>
                  <ul className="space-y-1">
                    {testResult.analytics.weak_topics?.map((topic, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Question-by-Question Analysis */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">📝 Question-wise Detailed Analysis</h2>
          <div className="space-y-4">
            {testResult.evaluated_answers?.map((qa, idx) => (
              <Card key={idx} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Question Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge>Question {idx + 1}</Badge>
                          <Badge variant={qa.score >= qa.max_marks * 0.7 ? "success" : "warning"}>
                            {qa.score}/{qa.max_marks} marks
                          </Badge>
                        </div>
                        <p className="text-slate-900 font-medium">{qa.question_text}</p>
                      </div>
                    </div>

                    {/* Your Answer */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Your Answer:</h4>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{qa.user_answer || "Not answered"}</p>
                      </div>
                    </div>

                    {/* Model Answer */}
                    {qa.model_answer && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Model Answer:</h4>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{qa.model_answer}</p>
                        </div>
                      </div>
                    )}

                    {/* AI Feedback */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">AI Evaluation Feedback:</h4>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-slate-700">{qa.feedback}</p>
                      </div>
                    </div>

                    {/* Strengths & Improvements */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {qa.strengths && qa.strengths.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            Strengths
                          </h4>
                          <ul className="space-y-1">
                            {qa.strengths.map((strength, i) => (
                              <li key={i} className="text-sm text-slate-700 pl-4">• {strength}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {qa.improvements && qa.improvements.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Suggestions for Improvement
                          </h4>
                          <ul className="space-y-1">
                            {qa.improvements.map((improvement, i) => (
                              <li key={i} className="text-sm text-slate-700 pl-4">• {improvement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Improvement Plan */}
        {testResult.improvement_plan && testResult.improvement_plan.length > 0 && (
          <Card className="border-indigo-200 bg-indigo-50">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">🎯 Personalized Improvement Plan</h2>
              <ul className="space-y-2">
                {testResult.improvement_plan.map((item, idx) => (
                  <li key={idx} className="text-sm text-slate-700 flex items-start gap-3">
                    <Zap className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center">
          <Button size="lg" onClick={() => setView("home")}>← Back to Home</Button>
          <Button size="lg" variant="outline" onClick={() => setView("generate")}>Take Another Test</Button>
          <Button size="lg" onClick={() => handleDownloadReport(testResult.id)}>
            <Download className="w-4 h-4 mr-2" />
            Download Full Report
          </Button>
        </div>
      </div>
    );
  }

  // History View
  if (view === "history") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">📚 Test History</h1>
            <p className="text-sm text-slate-600 mt-1">View and download your last 10 test attempts</p>
          </div>
          <Button onClick={() => setView("home")}>← Back</Button>
        </div>

        {attempts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No test attempts yet</p>
              <Button onClick={() => setView("generate")}>Generate Your First Test</Button>
            </CardContent>
          </Card>
        ) : (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Showing <span className="font-semibold">{attempts.length}</span> most recent test{attempts.length !== 1 ? 's' : ''}
              </p>
              <Badge variant="outline">{attempts.length} / 10</Badge>
            </div>
            <div className="space-y-4">
              {attempts.map((attempt, idx) => (
              <Card key={idx} className="border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 text-lg mb-2">{attempt.title || `Test ${idx + 1}`}</h3>
                      <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(attempt.submitted_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {attempt.time_spent_minutes} minutes
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {attempt.paper}
                        </span>
                        <Badge variant="outline" className="capitalize">{attempt.difficulty}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-3xl font-bold text-indigo-600">{Math.round(attempt.percentage)}%</p>
                        <p className="text-sm text-slate-600">{attempt.score}/{attempt.total_marks} marks</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setSelectedAttempt(attempt);
                            setTestResult(attempt);
                            setView("results");
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Analysis
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDownloadReport(attempt.id)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default TestSeriesPage;
